import { BREAKPOINTS, LoadingIndicator } from "@laerdal/life-react-components";
import axios from "axios";

import styled from "styled-components";
import { useEffect, useState } from "react";
import ProductApi from "../../services/api/ProductApi";
import { ProductLineDetailsDto } from "../../model/dto/productLineDetailsDto";
import { useParams } from "react-router-dom";
import ProductTopSection from "../../commonComponents/ProductTopSection";
import CantFindWhatYouNeed from "../../commonComponents/CantFindWhatYouNeed";
import CantFindCreateCase from "../../commonComponents/CantFindCreateCase";
import { PageWidthFull } from "../../commonComponents/PageWidthFull";
import { useMediaMatch } from "rooks";
import { useTranslation } from "react-i18next";
import posthog from 'posthog-js';
import { useSelector } from "react-redux";
import { buildMarketPath, selectMarket, selectTranslateWithAi } from "../../store/slices/globalConfigSlice";
import { marketList } from "../../model/constants/marketContants";
import ProductResources, { hasAnyProductResources } from "./components/ProductResources";
import ProductAssets from "./components/ProductAssets";
import TranslationBanner from "../../commonComponents/TranslationBanner";

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 32px;
    ${BREAKPOINTS.MEDIUM}{
        gap: 128px;
    }
`;

const FooterContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap:64px;
    margin-bottom: 64px;
`;

const Section = styled.div`
    scroll-margin-top: 72px;
`;

const BannerSection = styled.div`
    /* 64px below the tabs (32px wrapper gap + 32px) */
    margin-top: 32px;
    ${BREAKPOINTS.MEDIUM}{
        /* tabs bar already pulls the wrapper's 128px gap down to 64px */
        margin-top: 0;
        /* 32px to the section below (128px wrapper gap - 96px) */
        margin-bottom: -96px;
    }
`;

const sectionTabs = [
    { id: 'product-resources-section', label: 'Product resources' },
    { id: 'manage-your-product-section', label: 'Manage your product' },
    { id: 'faqs-section', label: 'FAQs' },
    { id: 'get-support-section', label: 'Get support' },
];

const ProductLinePage = () => {
    const { categoryLink, productLink } = useParams<{ categoryLink: string, productLink: string }>();
    const { t } = useTranslation("landingPage");

    const isMediumScreen = useMediaMatch(BREAKPOINTS.MEDIUM.replace('@media ', ''));
    const isMobile = !isMediumScreen; // Use existing pattern for mobile detection

    const [productLineLoading, setProductLineLoading] = useState<boolean>(true);
    const [productLine, setProductLine] = useState<ProductLineDetailsDto | undefined>(undefined);
    const [activeSection, setActiveSection] = useState<string>(sectionTabs[0].id);
    const [hasFaqArticles, setHasFaqArticles] = useState<boolean>(true);

    const currentMartket = useSelector(selectMarket);
    const translateWithAi = useSelector(selectTranslateWithAi);

    useEffect(() => {
        const onScroll = () => {
            // only spy on sections whose tabs are rendered
            const tabs = visibleTabs;
            if (tabs.length === 0) return;
            let current = tabs[0].id;
            let best = -Infinity;
            for (const tab of tabs) {
                const el = document.getElementById(tab.id);
                if (!el) continue;
                const top = el.getBoundingClientRect().top;
                // active = section whose top is closest above the threshold
                if (top <= 100 && top > best) {
                    best = top;
                    current = tab.id;
                }
            }
            setActiveSection(current);
        };
        // capture phase: the page scrolls in an inner container, not the window
        window.addEventListener('scroll', onScroll, { passive: true, capture: true });
        onScroll();
        return () => window.removeEventListener('scroll', onScroll, { capture: true });
        // eslint-disable-line
    }, [productLine, hasFaqArticles]);

    const scrollToSection = (id: string) => {
        posthog.capture('SUPP-ProductLinePage-SectiontabClick', {
            tab: id,
            currentUrl: window.location.href,
            productLineCode: productLink,
            productLineName: productLine?.productLineName,
        });
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    useEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;
        setProductLineLoading(true);
        setProductLine(undefined);

        if (categoryLink && productLink) {
            ProductApi
                .GetProductLine(productLink, (categoryLink != 'medical-devices' && translateWithAi) ? marketList[currentMartket].locale : 'en', signal, undefined, true)
                .then((data) => {
                    posthog.capture('SUPP-ProductPageView', {
                        page: data.displayName,
                        productLineCode: productLink
                    });

                    setProductLineLoading(false);
                    setProductLine(data);
                })
                .catch((error) => {
                    if (axios.isCancel(error)) return;
                })
        }

        return () => {
            controller.abort();
        };
    }, [categoryLink, productLink, translateWithAi, currentMartket]);

    const hasAnyResources = !!productLine && hasAnyProductResources(productLine);

    // hide tabs whose sections are not rendered
    const visibleTabs = sectionTabs.filter(tab => {
        if (tab.id === 'product-resources-section') return hasAnyResources;
        // software products have no physical assets to manage
        if (tab.id === 'manage-your-product-section') return !productLine?.isSoftware;
        if (tab.id === 'faqs-section') return hasFaqArticles;
        return true;
    });

    return (
        <>
            {productLineLoading &&
                <LoadingIndicator />
            }

            {productLine &&
                <Wrapper>
                    <ProductTopSection
                        title={productLine.displayName}
                        description={productLine.descripton}
                        asset={productLine.mainImageUrl}
                        breadcrumb={[{
                            text: t('Home'),
                            link: `/${currentMartket}`
                        }, {
                            text: t(productLine.categoryName),
                            link: buildMarketPath(currentMartket, `/category/${categoryLink}`)
                        }, {
                            text: productLine.displayName
                        }]}
                        buttonLink={productLine.productDownloadsUrl ?? undefined}
                        productLineCode={productLink}
                        tabs={visibleTabs.map((tab) => ({ id: tab.id, label: t(tab.label) }))}
                        activeTab={activeSection}
                        onTabClick={scrollToSection}
                    />

                    {/* page-level banner: shown whenever the page is translated, even when sections are hidden */}
                    {translateWithAi && marketList[currentMartket].locale != 'en' && categoryLink != 'medical-devices' &&
                        <BannerSection>
                            <PageWidthFull $useMaxWidth={true} $maxWidth={1024} $unsetMargin={true} $margin="0 auto">
                                <TranslationBanner />
                            </PageWidthFull>
                        </BannerSection>
                    }

                    {hasAnyResources &&
                        <Section id='product-resources-section'>
                            <ProductResources
                                currentMarket={currentMartket}
                                isMobile={isMobile}
                                categoryLink={categoryLink}
                                productLink={productLink}
                                knowledgeHubLink={productLine.knowledgeHubLink}
                                howToVideosLink={productLine.howToVideosLink}
                                userGuideLink={productLine.userGuideLink}
                                partsAndAccessoriesLink={productLine.partsAndAccessoriesLink}
                                hasDownloads={productLine.hasDownloads}
                                hasOnboarding={productLine.hasOnboarding}
                                hideTranslationBanner={true} />
                        </Section>
                    }

                    {!productLine.isSoftware &&
                        <Section id='manage-your-product-section'>
                            <ProductAssets
                                currentMarket={currentMartket}
                                categoryLink={categoryLink}
                                productLink={productLink}
                                productLineName={productLine.productLineName} />
                        </Section>
                    }

                    {hasFaqArticles &&
                        <Section id='faqs-section'>
                            <CantFindWhatYouNeed productLineCode={productLink} onHasArticlesChange={setHasFaqArticles} />
                        </Section>
                    }

                    <PageWidthFull $useMaxWidth={true} $maxWidth={1024} $margin="0 auto" >
                        <FooterContainer>
                            <Section id='get-support-section'>
                                <CantFindCreateCase />
                            </Section>
                        </FooterContainer>
                    </PageWidthFull>
                </Wrapper>
            }


        </>);
};

export default ProductLinePage;